import { Button, SelectItem, SelectListBox, SelectPopover, Tooltip } from '@compass/components'
import { cn } from '@compass/utils'
import { Column, VisibilityState } from '@tanstack/react-table'
import { Settings2 } from 'lucide-react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import {
  DialogTrigger,
  DropIndicator,
  Focusable,
  ListStateContext,
  Selection,
  TooltipTrigger,
  useDragAndDrop,
} from 'react-aria-components'
import { Key, useListData } from 'react-stately'

interface TableColumnSelectorProps {
  columnVisibility: VisibilityState
  onColumnVisibilityChange: (visibility: VisibilityState) => void
  columnOrder: string[]
  onColumnOrderChange: (order: string[]) => void
  columns: Column<any>[]
  disabledColumnIds?: Set<string>
  onColumnAdded?: () => void
  onColumnRemoved?: () => void
}

type TableColumnSelectorItem = {
  id: string
  label: string
}

function KeyboardFocusKeyObserver({
  activeDragKeyRef,
  isDraggingRef,
}: {
  activeDragKeyRef: React.MutableRefObject<Key | null>
  isDraggingRef: React.MutableRefObject<boolean>
}) {
  const context = useContext(ListStateContext)
  // Only update the drag key if we're NOT currently dragging
  if (!isDraggingRef.current) {
    activeDragKeyRef.current = context?.selectionManager.focusedKey ?? null
  }
  return null
}

export function TableColumnSelector({
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  columns,
  disabledColumnIds,
  onColumnAdded,
  onColumnRemoved,
}: TableColumnSelectorProps) {
  // Memoize column map for O(1) lookups instead of O(n) finds
  const columnMap = useMemo(() => new Map(columns.map((col) => [col.id, col])), [columns])
  const getColumnLabel = useCallback(
    (columnId: string): string => {
      const column = columnMap.get(columnId)
      if (!column) {
        return columnId
      }

      return typeof column.columnDef.header === 'string'
        ? column.columnDef.header
        : (column.columnDef.header?.toString?.() ?? columnId)
    },
    [columnMap]
  )

  const orderedColumns = useMemo(
    () => (columnOrder.length > 0 ? columnOrder : columns.map((c) => c.id)),
    [columnOrder, columns]
  )

  const visibleColumnIds = useMemo(() => {
    const visible: string[] = []
    columns.forEach((col) => {
      if (columnVisibility[col.id] === true) {
        visible.push(col.id)
      }
    })
    return visible
  }, [columnVisibility, columns])

  const onSelectionChange = useCallback(
    (selection: Selection) => {
      // Only update columns that changed visibility to avoid full table re-render
      // Use ref to get current visibility to avoid stale closures
      const newVisibility: VisibilityState = { ...columnVisibility }
      let hasChanges = false

      columns.forEach((col) => {
        // Prevent enabling disabled columns
        if (disabledColumnIds?.has(col.id)) {
          if (newVisibility[col.id] !== false) {
            newVisibility[col.id] = false
            hasChanges = true
          }
          return
        }
        const shouldBeVisible = selection === 'all' || selection.has(col.id) || col.columnDef.enableHiding === false
        if (newVisibility[col.id] !== shouldBeVisible) {
          newVisibility[col.id] = shouldBeVisible
          hasChanges = true
          if (shouldBeVisible) {
            onColumnAdded?.()
          } else {
            onColumnRemoved?.()
          }
        }
      })

      if (hasChanges) {
        onColumnVisibilityChange(newVisibility)
      }
    },
    [columnVisibility, columns, disabledColumnIds, onColumnAdded, onColumnRemoved, onColumnVisibilityChange]
  )

  const initialItems = useMemo(
    () =>
      orderedColumns.map((columnId) => ({
        id: columnId,
        label: getColumnLabel(columnId),
      })),
    [orderedColumns, getColumnLabel]
  )

  const list = useListData<TableColumnSelectorItem>({
    initialItems,
    getKey: (item) => item.id,
  })

  // Store list in a ref to avoid triggering effects on every render
  const listRef = useRef(list)
  listRef.current = list

  const activeDragKeyRef = useRef<Key | null>(null)
  const isDraggingRef = useRef(false)
  const { dragAndDropHooks } = useDragAndDrop<TableColumnSelectorItem>({
    getItems: (keys) => {
      // Mark that we're starting a drag operation
      isDraggingRef.current = true
      // Capture the dragged key at the START of drag operation
      const draggedKeys = [...keys].filter((key) => key === activeDragKeyRef.current)
      if (draggedKeys.length > 0) {
        activeDragKeyRef.current = draggedKeys[0]
      }
      return draggedKeys.map((key) => ({ 'text/plain': String(key) }))
    },
    onReorder(e) {
      const draggedKey = activeDragKeyRef.current
      if (!draggedKey) {
        return
      }

      // Get current order
      const currentOrder = list.items.map((item) => item.id)

      // Calculate new order based on drop position
      const draggedIndex = currentOrder.indexOf(String(draggedKey))
      const targetIndex = currentOrder.indexOf(String(e.target.key))

      if (draggedIndex === -1 || targetIndex === -1) {
        return
      }

      // Remove dragged item from current position
      const newOrder = [...currentOrder]
      newOrder.splice(draggedIndex, 1)

      // Calculate insert index
      // If dropping "before" target, insert at target's current index (in the array without dragged item)
      // If dropping "after" target, insert after target's current index (in the array without dragged item)
      let insertIndex = targetIndex

      // Adjust for the removed item
      if (draggedIndex < targetIndex) {
        insertIndex -= 1 // Target shifted left after removing dragged item
      }

      if (e.target.dropPosition === 'after') {
        insertIndex += 1 // Insert after target
      }

      newOrder.splice(insertIndex, 0, String(draggedKey))

      // Update internal list state
      if (e.target.dropPosition === 'before') {
        list.moveBefore(e.target.key, [draggedKey])
      } else if (e.target.dropPosition === 'after') {
        list.moveAfter(e.target.key, [draggedKey])
      }

      // Notify parent with the calculated new order
      onColumnOrderChange(newOrder)

      // Mark drag as complete
      isDraggingRef.current = false
    },
    onDragEnd() {
      // Reset drag state when drag ends (including cancellations)
      isDraggingRef.current = false
    },
    renderDropIndicator(target) {
      return <DropIndicator target={target} className='z-20 h-0.5 -mb-0.5 w-full bg-orange-700 rounded-full' />
    },
    getDropOperation: () => 'move',
  })

  // Force disabled columns to hidden when disabledColumnIds or columnVisibility changes
  useEffect(() => {
    if (!disabledColumnIds || disabledColumnIds.size === 0) {
      return
    }
    const updates: VisibilityState = {}
    for (const id of disabledColumnIds) {
      if (columnVisibility[id] !== false) {
        updates[id] = false
      }
    }
    if (Object.keys(updates).length > 0) {
      onColumnVisibilityChange({ ...columnVisibility, ...updates })
    }
  }, [disabledColumnIds, columnVisibility, onColumnVisibilityChange])

  // Track the previous columnOrder to detect external changes
  // useListData from react-stately returns an object with a new reference on each render.
  // By storing it in a ref and accessing it via listRef.current in the effect,
  // we prevent the effect's dependency array from triggering on every render,
  // breaking the cascading re-render cycle.
  const prevColumnOrderRef = useRef<string[]>(columnOrder)

  // Sync external columnOrder changes to internal list
  // This only runs when parent updates columnOrder (not during drag operations)
  useEffect(() => {
    const currentList = listRef.current
    const columnOrderStr = columnOrder.join(',')
    const prevColumnOrderStr = prevColumnOrderRef.current.join(',')

    // Only rebuild list if columnOrder actually changed from external source
    if (columnOrderStr !== prevColumnOrderStr) {
      const listOrder = currentList.items.map((item) => item.id)
      currentList.remove(...listOrder)
      const newColumns: Array<{ id: string; label: string }> = []

      columnOrder.forEach((columnId) => {
        const column = columnMap.get(columnId)
        if (!column) {
          return
        }

        newColumns.push({
          id: columnId,
          label: getColumnLabel(columnId),
        })
      })

      currentList.append(...newColumns)
      prevColumnOrderRef.current = columnOrder
    }
  }, [columnMap, columnOrder, columns, getColumnLabel])

  return (
    <DialogTrigger>
      <Button variant='ghost' alt aria-label='Select columns to display' data-testid='display-settings-trigger'>
        <Settings2 />
        Display
      </Button>
      <SelectPopover placement='bottom right'>
        <SelectListBox
          aria-label='Column order'
          items={list.items}
          selectedKeys={visibleColumnIds}
          selectionMode='multiple'
          onSelectionChange={onSelectionChange}
          dragAndDropHooks={dragAndDropHooks}
          className='min-w-56'
          autoFocus
        >
          {(item) => {
            const isColumnDisabled = disabledColumnIds?.has(item.id) ?? false
            const selectItem = (
              <SelectItem
                key={item.id}
                isDisabled={isColumnDisabled}
                onPointerDown={() => {
                  if (!isColumnDisabled) {
                    activeDragKeyRef.current = item.id
                  }
                }}
                textValue={item.label}
                className={cn('relative', isColumnDisabled && 'opacity-50 cursor-not-allowed')}
              >
                {item.label}
                {isColumnDisabled ? (
                  <TooltipTrigger delay={1000} closeDelay={0}>
                    <Focusable>
                      <button
                        type='button'
                        aria-label='Column not included in plan'
                        tabIndex={-1}
                        className='absolute border-0 bg-transparent p-0'
                        style={{
                          /* TODO: replace with '-inset-y-2 focus-visible:inset-0' after upgrading to Tailwind v4 */
                          top: '-0.5rem',
                          bottom: '-0.5rem',
                          left: '0',
                          right: '0',
                        }}
                      />
                    </Focusable>
                    <Tooltip placement='left' offset={16} className='max-w-64 duration-0 text-left'>
                      Not included in your plan. Please visit /plans to request an Enterprise trial.
                    </Tooltip>
                  </TooltipTrigger>
                ) : null}
                <KeyboardFocusKeyObserver activeDragKeyRef={activeDragKeyRef} isDraggingRef={isDraggingRef} />
              </SelectItem>
            )

            return selectItem
          }}
        </SelectListBox>
      </SelectPopover>
    </DialogTrigger>
  )
}
